/**
 * THE ORDER RAIL'S ROWS - one list that is both the turn order and the
 * roster.
 *
 * The centre panel used to show the same people twice: tracker chips in
 * initiative order above roster tabs in roster order, two rows with two
 * meanings for one table of people. The rail (v8.317.0) is ONE column,
 * and this module decides what goes in it and in what order:
 *
 *   1. the tracker's participants, in the tracker's order - a participant
 *      is a row whether or not a roster member stands behind it (a custom
 *      "Warp Storm" is a row; so is a ghost whose combatant has left)
 *   2. then everyone on the roster who is NOT in the order, in roster
 *      order, under a divider the component draws
 *
 * With the tracker off, nobody is in the order and the rail is simply the
 * roster. The marks - who I am, who I am shooting - survive either way.
 *
 * A row carries everything the component needs and nothing it derives:
 * the projected Target (for vitals), the livery (from the roster's teams),
 * the initiative and its parts (so the row can explain "1d10(6) + AB 4").
 * Nothing here is stored; the rail is a projection over the tracker
 * document and the roster.
 *
 * Pure, zero React dependency.
 */
import type { Tab, TurnParticipant, ParticipantStatus, Target } from '../../types';
import { isDowned } from '../conditions';
import type { Livery } from './team';

export interface RailRow {
  /** Stable key: the participant's id when in the order, else `tab-<id>`. */
  key: string;
  combatantId: string | null;
  /** The tracker participant behind this row, or null when not in the order. */
  participantId: string | null;
  name: string;
  initiative: number | null;
  initiativeRoll: number | null;
  initiativeBonus: number | null;
  initiativeManual: boolean;
  /** The tracker's word on this row, or null when not in the order. */
  status: ParticipantStatus | null;
  inOrder: boolean;
  isCustom: boolean;
  /** Dead, unconscious or dying AND the house rule skips them. */
  downed: boolean;
  /** Surprised: sits out THIS round (the round a fight was sprung in), then acts as anyone. */
  surprised: boolean;
  /** Hidden from the players (plan map-display, G1) - only a GM screen holds such a row. */
  hidden: boolean;
  isActor: boolean;
  isTarget: boolean;
  livery: Livery | null;
  /** The live projection, for the vitals gauge; null for a custom row or a ghost. */
  target: Target | null;
}

export interface RailInput {
  tabs: readonly Tab[];
  participants: readonly TurnParticipant[];
  trackerActive: boolean;
  actorId: string | null | undefined;
  targetId: string | null | undefined;
  liveries: Readonly<Record<string, Livery | undefined>>;
  skipDownedTurns: boolean;
  /** Who sits out this round, by combatant id (`sittingOut` in engine/turnTracker). Absent: nobody. */
  sittingOut?: ReadonlySet<string>;
  /** The GM's hidden figures, by combatant id. Absent: nobody is hidden. */
  hiddenIds?: ReadonlySet<string>;
}

function rowFromTab(tab: Tab, p: TurnParticipant | null, input: RailInput): RailRow {
  return {
    key: p ? p.id : `tab-${tab.id}`,
    combatantId: tab.id,
    participantId: p ? p.id : null,
    // The roster's name wins: the tracker's copy goes stale on a rename.
    name: tab.target.name || tab.label || p?.name || tab.id,
    initiative: p ? p.initiative : null,
    initiativeRoll: p ? p.initiativeRoll : null,
    initiativeBonus: p ? p.initiativeBonus : null,
    initiativeManual: !!p?.initiativeManual,
    status: p ? p.status : null,
    inOrder: !!p,
    isCustom: false,
    downed: input.skipDownedTurns && isDowned(tab.target.conditions),
    surprised: !!p && !!input.sittingOut?.has(tab.id),
    hidden: !!input.hiddenIds?.has(tab.id),
    isActor: tab.id === input.actorId,
    isTarget: tab.id === input.targetId,
    livery: input.liveries[tab.id] ?? null,
    target: tab.target,
  };
}

/** A custom participant, or a ghost whose combatant has left the roster. */
function rowFromParticipant(p: TurnParticipant): RailRow {
  return {
    key: p.id,
    combatantId: p.combatantId,
    participantId: p.id,
    name: p.name,
    initiative: p.initiative,
    initiativeRoll: p.initiativeRoll,
    initiativeBonus: p.initiativeBonus,
    initiativeManual: !!p.initiativeManual,
    status: p.status,
    inOrder: true,
    isCustom: !!p.isCustom,
    downed: false,
    surprised: false,
    hidden: false,
    isActor: false,
    isTarget: false,
    livery: null,
    target: null,
  };
}

/**
 * The rows, in rail order. See the header for the two runs.
 */
export function railRows(input: RailInput): RailRow[] {
  const byId = new Map(input.tabs.map((t) => [t.id, t]));
  const placed = new Set<string>();
  const tracked = (input.trackerActive ? (input.participants ?? []) : []).map((p) => {
    const tab = p.combatantId ? byId.get(p.combatantId) : undefined;
    if (!tab) return rowFromParticipant(p);
    placed.add(tab.id);
    return rowFromTab(tab, p, input);
  });
  const rest = input.tabs.filter((tab) => !placed.has(tab.id)).map((tab) => rowFromTab(tab, null, input));
  return [...tracked, ...rest];
}

/** The row whose turn it is, or undefined with the tracker off (the strip and the turn card over the map read it). */
export const actingRow = (rows: readonly RailRow[]): RailRow | undefined => rows.find((r) => r.status === 'active');
